import { zodResolver } from "@hookform/resolvers/zod";
import EditIcon from "@mui/icons-material/Edit";
import EditOffIcon from "@mui/icons-material/EditOff";
import SaveIcon from '@mui/icons-material/Save';
import { Autocomplete, Box, CircularProgress, Grid2, IconButton, InputAdornment, Paper, TextField, Tooltip, Typography } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useErrorHandler } from "../../../hooks/Error/useErrorHandler";
import { useFormStyles } from "../../../hooks/useFormStyles";
import { getLicensingFormSchema, LicensingForm as LicensingFormType } from "../../../types/forms/capture";
import { Licensing } from "../../../types/model/licensing";

interface LicensingFormProps {
  licensingData?: Licensing | null;
  canEdit: boolean;
  onSubmit: (values: LicensingFormType) => Promise<void>;
}

export default function LicensingForm({
  licensingData,
  canEdit,
  onSubmit,
}: Readonly<LicensingFormProps>) {
  const { t } = useTranslation();
  const handleError = useErrorHandler();
  const [isUpdateForm, setIsUpdateForm] = useState<boolean>(!!licensingData);
  const [isEditing, setIsEditing] = useState<boolean>(!isUpdateForm);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const formStyles = useFormStyles(isEditing);

  const validationSchema = getLicensingFormSchema(t);

  const methods = useForm({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      recommendedPumpingRate: licensingData?.recommendedPumpingRate ?? null,
      annualVolume: licensingData?.annualVolume ?? null,
      licenseNo: licensingData?.licenseNo || "",
      licenseIssue: licensingData?.licenseIssue || undefined,
      licenseExpiry: licensingData?.licenseExpiry || undefined,
      consumptionMonth: licensingData?.consumptionMonth ?? null,
      hoursPerDay: licensingData?.hoursPerDay ?? null,
      daysPerMonth: licensingData?.daysPerMonth ?? null,
      monthsPerYear: licensingData?.monthsPerYear ?? null,
    },
  });

  const handleFormSubmit = async (values: LicensingFormType) => {
    try {
      setIsSaving(true);
      await onSubmit(values);
      if (isUpdateForm) setIsEditing(false);
    } catch (error) {
      handleError(error);
    } finally {
      setIsSaving(false);
    } 
  };

  // Sync form state when licensingData changes
  useEffect(() => {
    setIsUpdateForm(!!licensingData);
    if (licensingData) {
      setIsEditing(false);
    } else {
      setIsEditing(true);
    }
  }, [licensingData]);

  return (
    <FormProvider {...methods}>
      <Box component="form" onSubmit={methods.handleSubmit(handleFormSubmit)}>
        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 12 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 3,
              }}
            >
              <Typography variant="h5">
                {t("captures.capture.licensing.title")}
              </Typography>

              {/* Action Icons */}
              {isEditing && canEdit ? ( 
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Tooltip title={t("generic.save")}>
                    <IconButton
                      color="success"
                      onClick={methods.handleSubmit(handleFormSubmit)}
                      disabled={isSaving || !canEdit}
                    >
                      {isSaving ? <CircularProgress size={20} /> : <SaveIcon />}
                    </IconButton>
                  </Tooltip>

                  <Tooltip title={t("generic.cancel")}>
                    <IconButton
                      color="error"
                      onClick={() => {
                        setIsEditing(false);
                        methods.reset();
                      }}
                    >
                      <EditOffIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              ) : (
                canEdit && (
                  <Tooltip title={t("generic.edit")}>
                    <IconButton
                      color="primary"
                      onClick={() => setIsEditing(true)}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                )
              )}
            </Box>
          </Grid2>

          <Grid2 size={12}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 2 }}>
              <Typography
                variant="subtitle1"
                color="text.secondary"
                sx={{ fontWeight: 600, mb: 2 }}
              >
                {t("captures.capture.licensing.info")}
              </Typography>

              <Grid2 container spacing={2}>
                <Grid2 size={4}>
                  <TextField
                    fullWidth
                    label={t("captures.capture.licensing.license-no")}                         
                    variant={(!isEditing || !canEdit) ? "standard" : "outlined"}
                    disabled={!isEditing || isSaving || !canEdit}
                    sx={{ ...formStyles }}
                    {...methods.register("licenseNo")}
                    error={!!methods.formState.errors.licenseNo}
                    helperText={methods.formState.errors.licenseNo?.message}
                  />
                </Grid2>

                <Grid2 size={4}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Controller
                      name="licenseIssue"
                      control={methods.control}
                      render={({ field }) => (
                        <DatePicker
                          label={t("captures.capture.licensing.license-issue")}
                          value={field.value ? dayjs(field.value, "YYYY-MM-DD") : null}
                          onChange={(date) => {
                            field.onChange(date ? date.format("YYYY-MM-DD") : undefined);
                          }}
                          format="DD/MM/YYYY"
                          disabled={!isEditing || isSaving || !canEdit}
                          sx={{ ...formStyles }}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              variant: (!isEditing || !canEdit) ? "standard" : "outlined",
                              placeholder: "DD/MM/YYYY",
                              error: !!methods.formState.errors.licenseIssue,
                              helperText: methods.formState.errors.licenseIssue?.message,
                            },
                          }}
                        />
                      )}
                    />
                  </LocalizationProvider>
                </Grid2>

                <Grid2 size={4}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Controller
                      name="licenseExpiry"
                      control={methods.control}
                      render={({ field }) => (
                        <DatePicker
                          label={t("captures.capture.licensing.license-expiry")}
                          value={field.value ? dayjs(field.value, "YYYY-MM-DD") : null}
                          onChange={(date) => {
                            field.onChange(date ? date.format("YYYY-MM-DD") : undefined);
                          }}
                          format="DD/MM/YYYY"
                          disabled={!isEditing || isSaving || !canEdit}
                          sx={{ ...formStyles }}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              variant: (!isEditing || !canEdit) ? "standard" : "outlined",
                              placeholder: "DD/MM/YYYY",
                              error: !!methods.formState.errors.licenseExpiry,
                              helperText: methods.formState.errors.licenseExpiry?.message,
                            },
                          }}
                        />
                      )}
                    />
                  </LocalizationProvider>
                </Grid2>
              </Grid2>
            </Paper>
          </Grid2>

          <Grid2 size={12}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
              <Typography 
                variant="subtitle1"
                color="text.secondary"
                sx={{ fontWeight: 600, mb: 2 }}
              >
                {t("captures.capture.licensing.usage-info")}
              </Typography>
              <Grid2 container spacing={2}>
                <Grid2 size={4}>
                  <TextField
                    fullWidth
                    label={t("captures.capture.licensing.recommended-pumping-rate")}                         
                    variant={(!isEditing || !canEdit) ? "standard" : "outlined"}
                    disabled={!isEditing || isSaving || !canEdit}
                    sx={{ ...formStyles }}
                    type="text"
                    inputMode="decimal"
                    {...methods.register("recommendedPumpingRate")}
                    slotProps={{
                      input: {
                        endAdornment: <InputAdornment position="end">{t("generic.units.l-per-s")}</InputAdornment>,
                      },
                    }}
                    error={!!methods.formState.errors.recommendedPumpingRate}
                    helperText={methods.formState.errors.recommendedPumpingRate?.message}
                  />
                </Grid2>

                <Grid2 size={4}>
                  <TextField
                    fullWidth
                    label={t("captures.capture.licensing.annual-volume")}                         
                    variant={(!isEditing || !canEdit) ? "standard" : "outlined"}
                    disabled={!isEditing || isSaving || !canEdit}
                    sx={{ ...formStyles }}
                    type="text"
                    inputMode="decimal"
                    {...methods.register("annualVolume")}
                    slotProps={{
                      input: {
                        endAdornment: <InputAdornment position="end">{t("generic.units.m3-per-year")}</InputAdornment>,
                      },
                    }}
                    error={!!methods.formState.errors.annualVolume}
                    helperText={methods.formState.errors.annualVolume?.message}
                  />
                </Grid2>

                <Grid2 size={4}>
                  <Controller
                    name="consumptionMonth"
                    control={methods.control}
                    render={({ field }) => (
                      <Autocomplete
                        options={Array.from({ length: 12 }, (_, i) => i + 1)}
                        getOptionLabel={(month) => t(`generic.months.${month}`)}
                        value={field.value ?? null}
                        onChange={(event, newValue) => field.onChange(newValue)}
                        disabled={!isEditing || isSaving || !canEdit}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={t("captures.capture.licensing.consumption-month")}
                            variant={(!isEditing || !canEdit) ? "standard" : "outlined"}
                            sx={{ ...formStyles }}
                            error={!!methods.formState.errors.consumptionMonth}
                            helperText={methods.formState.errors.consumptionMonth?.message}
                          />
                        )}
                      />
                    )}
                  />
                </Grid2>

                <Grid2 size={4}>
                  <TextField
                    fullWidth
                    label={t("captures.capture.licensing.hours-per-day")}                         
                    variant={(!isEditing || !canEdit) ? "standard" : "outlined"}
                    disabled={!isEditing || isSaving || !canEdit}
                    sx={{ ...formStyles }}
                    type="text"
                    inputMode="decimal"
                    {...methods.register("hoursPerDay")}
                    slotProps={{
                      input: {
                        endAdornment: <InputAdornment position="end">{t("generic.units.h")}</InputAdornment>,
                      },
                    }}
                    error={!!methods.formState.errors.hoursPerDay}
                    helperText={methods.formState.errors.hoursPerDay?.message}
                  />
                </Grid2>

                <Grid2 size={4}>
                  <TextField
                    fullWidth
                    label={t("captures.capture.licensing.days-per-month")}                         
                    variant={(!isEditing || !canEdit) ? "standard" : "outlined"}
                    disabled={!isEditing || isSaving || !canEdit}
                    sx={{ ...formStyles }}
                    type="text"
                    inputMode="decimal"
                    {...methods.register("daysPerMonth")}
                    slotProps={{
                      input: {
                        endAdornment: <InputAdornment position="end">{t("generic.units.days")}</InputAdornment>,
                      },
                    }}
                    error={!!methods.formState.errors.daysPerMonth}
                    helperText={methods.formState.errors.daysPerMonth?.message}
                  />
                </Grid2>

                <Grid2 size={4}>
                  <TextField
                    fullWidth
                    label={t("captures.capture.licensing.months-per-year")}                         
                    variant={(!isEditing || !canEdit) ? "standard" : "outlined"}
                    disabled={!isEditing || isSaving || !canEdit}
                    sx={{ ...formStyles }}
                    type="text"
                    inputMode="decimal"
                    {...methods.register("monthsPerYear")}
                    slotProps={{
                      input: {
                        endAdornment: <InputAdornment position="end">{t("generic.units.months")}</InputAdornment>,
                      },
                    }}
                    error={!!methods.formState.errors.monthsPerYear}
                    helperText={methods.formState.errors.monthsPerYear?.message}
                  />
                </Grid2>
              </Grid2>
            </Paper>
          </Grid2>
        </Grid2>
      </Box>
    </FormProvider>
  );
}